import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { subDays } from 'date-fns';

const WeekContext = createContext(null);

export const WeekProvider = ({ children }) => {
    // Store the current user's preferred default days
    const [userPreferredDays, setUserPreferredDays] = useState(30);
    
    // Initialize with a default range, but this will be updated based on user preferences
    const [dateRange, setDateRange] = useState(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const defaultStart = new Date(today);
        defaultStart.setDate(today.getDate() - 6); // Default 7 days for initial load
        return { 
            start: defaultStart, 
            end: today 
        };
    });

    // Function to set default range based on user preference
    const setDefaultRangeFromPreference = useCallback((days = 30) => {
        // Update the stored user preference
        setUserPreferredDays(days);
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const start = subDays(today, days - 1); // days - 1 because we include today
        start.setHours(0, 0, 0, 0);
        
        setDateRange({ start, end: today });
    }, []);

    // Function to reset to default range (can be called by WeekPicker)
    // Uses the stored user preference automatically
    const resetToDefaultRange = useCallback(() => {
        setDefaultRangeFromPreference(userPreferredDays);
    }, [userPreferredDays, setDefaultRangeFromPreference]);

    // Function to check if current range matches user's default preference
    const isCurrentRangeDefault = useCallback(() => {
        if (!dateRange.start || !dateRange.end) return false;
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const expectedStart = subDays(today, userPreferredDays - 1);
        expectedStart.setHours(0, 0, 0, 0);
        
        // Check if current range matches expected range (within same day)
        const isSameDay = (date1, date2) => {
            return (
                date1.getFullYear() === date2.getFullYear() &&
                date1.getMonth() === date2.getMonth() &&
                date1.getDate() === date2.getDate()
            );
        };
        
        return isSameDay(dateRange.start, expectedStart) && isSameDay(dateRange.end, today);
    }, [dateRange.start, dateRange.end, userPreferredDays]);

    return (
        <WeekContext.Provider value={{ 
            dateRange, 
            setDateRange, 
            setDefaultRangeFromPreference,
            resetToDefaultRange,
            isCurrentRangeDefault
        }}>
            {children}
        </WeekContext.Provider>
    );
};

export const useWeek = () => useContext(WeekContext);
